<template>
  <q-page class="community-page">
    <div class="community-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div>
          <q-skeleton type="text" width="180px" dark style="font-size: 1.75rem;" />
          <q-skeleton type="text" width="300px" dark style="margin-top: 4px;" />
        </div>
        <div class="stats-grid" style="margin-top: 24px;">
          <q-skeleton v-for="n in 4" :key="n" type="rect" height="80px" dark style="border-radius: 12px;" />
        </div>
        <q-skeleton type="rect" height="400px" dark style="border-radius: 12px; margin-top: 24px;" />
        <q-skeleton type="rect" height="200px" dark style="border-radius: 12px; margin-top: 24px;" />
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="community-empty">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchData"
        />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="community-header">
          <h1 class="community-title">Comunidad</h1>
          <p class="community-subtitle">La comunidad de Olympic Academy</p>
        </div>

        <!-- Stats bar -->
        <div class="stats-grid">
          <q-card flat class="stat-card">
            <q-card-section>
              <p class="stat-label">Miembros</p>
              <p class="stat-value">{{ stats.totalUsers }}</p>
            </q-card-section>
          </q-card>
          <q-card flat class="stat-card">
            <q-card-section>
              <p class="stat-label">Logros desbloqueados</p>
              <p class="stat-value">{{ stats.totalAchievements }}</p>
            </q-card-section>
          </q-card>
          <q-card flat class="stat-card">
            <q-card-section>
              <p class="stat-label">Top XP</p>
              <p class="stat-value">{{ wallOfFame.length > 0 ? wallOfFame[0].xp : 0 }}</p>
            </q-card-section>
          </q-card>
          <q-card flat class="stat-card">
            <q-card-section>
              <p class="stat-label">Sedes</p>
              <p class="stat-value">{{ groups.length }}</p>
            </q-card-section>
          </q-card>
        </div>

        <!-- Wall of Fame -->
        <q-card flat class="section-card">
          <q-card-section>
            <h2 class="section-title">Wall of Fame</h2>
            <div class="wall-list">
              <div
                v-for="user in wallOfFame"
                :key="user.rank"
                class="wall-row"
                :class="{ 'wall-row-top': user.rank <= 3 }"
              >
                <!-- Rank -->
                <span class="wall-rank">
                  <q-icon
                    v-if="user.rank === 1"
                    name="emoji_events"
                    size="24px"
                    style="color: #ffd700;"
                  />
                  <q-icon
                    v-else-if="user.rank === 2"
                    name="emoji_events"
                    size="24px"
                    style="color: #c0c0c0;"
                  />
                  <q-icon
                    v-else-if="user.rank === 3"
                    name="emoji_events"
                    size="24px"
                    style="color: #cd7f32;"
                  />
                  <span v-else class="wall-rank-num">#{{ user.rank }}</span>
                </span>

                <!-- Avatar -->
                <div class="wall-avatar">
                  {{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}
                </div>

                <!-- Info -->
                <div class="wall-info">
                  <p class="wall-name">{{ user.firstName }} {{ user.lastName }}</p>
                  <p class="wall-meta">
                    {{ user.role.replace(/_/g, ' ') }} | Racha: {{ user.currentStreak }} dias
                  </p>
                </div>

                <!-- XP + Level -->
                <div class="wall-xp">
                  <p class="wall-xp-value">{{ user.xp }} XP</p>
                  <p class="wall-level">{{ user.levelName }}</p>
                </div>
              </div>
            </div>
          </q-card-section>
        </q-card>

        <!-- Activity feed -->
        <q-card flat class="section-card">
          <q-card-section>
            <h2 class="section-title">Actividad reciente</h2>

            <div v-if="activity.length === 0" class="activity-empty">
              <p>Todavia no hay actividad en la comunidad.</p>
            </div>

            <div v-else class="activity-list">
              <div
                v-for="(item, idx) in activity"
                :key="idx"
                class="activity-item"
              >
                <!-- Achievement unlock -->
                <template v-if="item.type === 'achievement'">
                  <div class="activity-icon achievement-icon">
                    <q-icon :name="item.icon || 'trophy'" size="20px" />
                  </div>
                  <div class="activity-content">
                    <p class="activity-text">
                      <strong>{{ item.userName }}</strong> desbloqueo
                      <span class="activity-highlight">{{ item.achievementName }}</span>
                    </p>
                    <p v-if="item.greekName" class="activity-greek">{{ item.greekName }}</p>
                    <p class="activity-meta">
                      {{ formatDate(item.date) }} | +{{ item.xpAwarded }} XP
                    </p>
                  </div>
                </template>

                <!-- AURA event -->
                <template v-else>
                  <div class="activity-icon aura-icon">
                    <q-icon name="bolt" size="20px" />
                  </div>
                  <div class="activity-content">
                    <p class="activity-text">
                      <strong>{{ item.userName }}</strong> gano
                      <span class="activity-highlight">{{ item.points }} AURA</span>
                    </p>
                    <p class="activity-source">{{ item.source }} — {{ item.description }}</p>
                    <p class="activity-meta">{{ formatDate(item.date) }}</p>
                  </div>
                </template>
              </div>
            </div>
          </q-card-section>
        </q-card>

        <!-- WhatsApp Groups -->
        <q-card flat class="section-card">
          <q-card-section>
            <h2 class="section-title">Grupos de WhatsApp</h2>
            <p class="section-subtitle">
              Unite a los grupos de WhatsApp de tu sede para conectar con otros entrenadores.
            </p>
            <q-list class="groups-list">
              <q-item v-for="group in groups" :key="group.name" class="group-item">
                <q-item-section avatar>
                  <q-icon name="chat" size="24px" style="color: var(--oa-gold-full);" />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="group-name">El Templo {{ group.name }}</q-item-label>
                  <q-item-label caption class="group-city">{{ group.city }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge outline label="Proximamente" style="color: var(--oa-text-muted); border-color: var(--oa-border);" />
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { api } from 'boot/axios';

// --- Types ---

interface WallOfFameUser {
  rank: number;
  firstName: string;
  lastName: string;
  role: string;
  xp: number;
  level: number;
  levelName: string;
  currentStreak: number;
  sedeId: string | null;
}

interface ActivityItem {
  type: 'achievement' | 'aura';
  userName: string;
  date: string;
  achievementName?: string;
  greekName?: string | null;
  icon?: string | null;
  xpAwarded?: number;
  source?: string;
  points?: number;
  description?: string;
}

interface ActivityResponse {
  items: ActivityItem[];
  totalUsers: number;
  totalAchievements: number;
}

interface GroupItem {
  name: string;
  city: string;
  whatsappLink: string | null;
}

// --- State ---

const loading = ref(true);
const error = ref<string | null>(null);
const wallOfFame = ref<WallOfFameUser[]>([]);
const activity = ref<ActivityItem[]>([]);
const groups = ref<GroupItem[]>([]);
const stats = ref({ totalUsers: 0, totalAchievements: 0 });

// --- Helpers ---

function formatDate(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'hace un momento';
  if (diffMin < 60) return `hace ${diffMin} minuto${diffMin === 1 ? '' : 's'}`;
  if (diffHours < 24) return `hace ${diffHours} hora${diffHours === 1 ? '' : 's'}`;
  if (diffDays < 30) return `hace ${diffDays} dia${diffDays === 1 ? '' : 's'}`;
  return date.toLocaleDateString('es-AR');
}

// --- Data fetching ---

async function fetchData() {
  loading.value = true;
  error.value = null;

  try {
    const [wallRes, activityRes, groupsRes] = await Promise.all([
      api.get<WallOfFameUser[]>('/community/wall-of-fame'),
      api.get<ActivityResponse>('/community/activity'),
      api.get<GroupItem[]>('/community/groups'),
    ]);

    wallOfFame.value = wallRes.data;
    activity.value = activityRes.data.items;
    groups.value = groupsRes.data;
    stats.value = {
      totalUsers: activityRes.data.totalUsers,
      totalAchievements: activityRes.data.totalAchievements,
    };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Error desconocido';
    error.value = `No se pudo cargar la comunidad: ${msg}`;
  } finally {
    loading.value = false;
  }
}

onMounted(fetchData);
</script>

<style scoped>
.community-page {
  min-height: 100vh;
  background: var(--oa-bg-1);
}

.community-container {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px;
}

.community-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-text {
  color: var(--oa-text-muted);
  margin-top: 12px;
  font-size: 0.95rem;
}

/* Header */
.community-header {
  margin-bottom: 24px;
}

.community-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.community-subtitle {
  color: var(--oa-text-muted);
  margin: 4px 0 0;
  font-size: 0.95rem;
}

/* Stats grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.stat-card {
  background: var(--oa-bg-2) !important;
  border: 1px solid var(--oa-border);
  border-radius: 12px;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--oa-text-muted);
  margin: 0 0 4px;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 0;
}

/* Section cards */
.section-card {
  background: var(--oa-bg-2) !important;
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px;
}

.section-subtitle {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: -8px 0 16px;
}

/* Wall of Fame */
.wall-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wall-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background 0.15s;
}

.wall-row-top {
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent);
}

.wall-rank {
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wall-rank-num {
  font-weight: 700;
  color: var(--oa-text-muted);
  font-size: 0.9rem;
}

.wall-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--oa-gold-full) 20%, transparent);
  color: var(--oa-gold-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.wall-info {
  flex: 1;
  min-width: 0;
}

.wall-name {
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
  font-size: 0.95rem;
}

.wall-meta {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
  text-transform: capitalize;
}

.wall-xp {
  text-align: right;
  flex-shrink: 0;
}

.wall-xp-value {
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 0;
  font-size: 0.95rem;
}

.wall-level {
  font-size: 0.7rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

/* Activity feed */
.activity-empty {
  text-align: center;
  padding: 32px 0;
  color: var(--oa-text-muted);
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid var(--oa-border);
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.achievement-icon {
  background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  color: var(--oa-gold-full);
}

.aura-icon {
  background: color-mix(in srgb, #7c3aed 15%, transparent);
  color: #a78bfa;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-text {
  color: var(--oa-text-primary);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.activity-highlight {
  font-weight: 600;
  color: var(--oa-gold-full);
}

.activity-greek {
  font-style: italic;
  color: var(--oa-gold-full);
  font-size: 0.8rem;
  margin: 2px 0 0;
}

.activity-source {
  font-size: 0.8rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
  text-transform: capitalize;
}

.activity-meta {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

/* Groups */
.groups-list {
  padding: 0;
}

.group-item {
  border-radius: 8px;
  margin-bottom: 4px;
}

.group-name {
  color: var(--oa-text-primary) !important;
  font-weight: 500;
}

.group-city {
  color: var(--oa-text-muted) !important;
}
</style>
